/**
 * FEAT-015 — Five seeded vehicle profiles.
 *
 * The seeder is idempotent: it inserts each preset only if no profile with
 * the same name exists yet. Users can edit or delete presets; deleted
 * presets are NOT re-seeded on next boot (the de-dupe is by name, but once
 * deleted the row is just gone — the seeder runs only on truly empty
 * libraries so a deletion sticks).
 *
 * Numbers are sourced from public EV spec sheets (battery kWh, peak AC kW,
 * peak DC kW). The DC fast-charge presets also carry a realistic `chargingCurve`
 * (FEAT-010) that tapers power toward the top of the pack; AC-only presets and
 * the plain ones leave it off and use the flat `maxDcPowerKw` cap.
 */
import type { ChargingCurvePoint, VehicleProfileSnapshot } from '@/domain';
import type { VehicleRepository } from '@/persistence/ports';
import { newVehicleProfileId } from '@/domain/newVehicleProfileId';

export interface VehiclePresetSpec {
  readonly name: string;
  readonly batteryKwh: number;
  readonly maxAcPowerKw: number;
  readonly maxDcPowerKw: number;
  readonly defaultStartSocPct: number;
  /** Optional DC taper curve (peak then roll-off toward 100% SoC). */
  readonly chargingCurve?: readonly ChargingCurvePoint[];
}

/**
 * Frozen so call sites can't accidentally mutate. Order is the order the
 * presets appear in VehiclesPage.
 */
export const VEHICLE_PRESETS: readonly VehiclePresetSpec[] = Object.freeze([
  {
    name: 'Tesla Model 3 LR',
    batteryKwh: 75,
    maxAcPowerKw: 11,
    maxDcPowerKw: 250,
    defaultStartSocPct: 20,
    // Peaks ~250 kW low, tapers hard after ~50% (classic Tesla LFP/NCA curve).
    chargingCurve: [
      { socPct: 0, powerKw: 250 },
      { socPct: 20, powerKw: 230 },
      { socPct: 50, powerKw: 150 },
      { socPct: 80, powerKw: 70 },
      { socPct: 100, powerKw: 20 },
    ],
  },
  {
    name: 'Hyundai Ioniq 5',
    batteryKwh: 77,
    maxAcPowerKw: 11,
    maxDcPowerKw: 220,
    defaultStartSocPct: 20,
    // 800V architecture holds peak longer, then steps down past ~80%.
    chargingCurve: [
      { socPct: 0, powerKw: 220 },
      { socPct: 45, powerKw: 220 },
      { socPct: 70, powerKw: 150 },
      { socPct: 80, powerKw: 100 },
      { socPct: 100, powerKw: 25 },
    ],
  },
  {
    name: 'Porsche Taycan 4S',
    batteryKwh: 84,
    maxAcPowerKw: 22,
    maxDcPowerKw: 270,
    defaultStartSocPct: 20,
    chargingCurve: [
      { socPct: 0, powerKw: 270 },
      { socPct: 40, powerKw: 250 },
      { socPct: 70, powerKw: 160 },
      { socPct: 85, powerKw: 90 },
      { socPct: 100, powerKw: 25 },
    ],
  },
  {
    name: 'Demo 800V HPC',
    batteryKwh: 110,
    maxAcPowerKw: 22,
    maxDcPowerKw: 650,
    defaultStartSocPct: 10,
    // Aggressive HPC demo: huge peak, steep taper for a visible curve effect.
    chargingCurve: [
      { socPct: 0, powerKw: 650 },
      { socPct: 30, powerKw: 600 },
      { socPct: 55, powerKw: 350 },
      { socPct: 80, powerKw: 150 },
      { socPct: 100, powerKw: 40 },
    ],
  },
  {
    name: 'Generic AC Only',
    batteryKwh: 60,
    maxAcPowerKw: 22,
    maxDcPowerKw: 0,
    defaultStartSocPct: 30,
  },
]);

/**
 * Seed the workspace vehicle library iff it is empty.
 *
 * Returns the number of rows actually inserted (0 if the library already had
 * any rows). Callers can show "Loaded 5 starter vehicles" once after first
 * boot — see VehiclesPage's initial-load effect.
 */
export async function seedVehiclePresetsIfEmpty(
  repo: VehicleRepository,
  now: () => number = Date.now,
): Promise<number> {
  const existing = await repo.list();
  if (existing.length > 0) return 0;
  let inserted = 0;
  for (const spec of VEHICLE_PRESETS) {
    const snap: VehicleProfileSnapshot = {
      id: newVehicleProfileId(),
      name: spec.name,
      batteryKwh: spec.batteryKwh,
      maxAcPowerKw: spec.maxAcPowerKw,
      maxDcPowerKw: spec.maxDcPowerKw,
      defaultStartSocPct: spec.defaultStartSocPct,
      ...(spec.chargingCurve ? { chargingCurve: spec.chargingCurve } : {}),
      isPreset: true,
      createdAt: now(),
    };
    await repo.upsert(snap);
    inserted++;
  }
  return inserted;
}
